import http from "@/services/http";

export default {
  namespaced: true,
  state: {
    messages: [],
    isTyping: false,
    activePatientId: null,
    chatInitiated: false,
    patientPhone: null,
    // salvia-803: enquanto o historico nao volta, NAO se sabe se o paciente tem
    // telefone. Sem este estado a tela afirmava "Paciente sem telefone" antes de
    // ter olhado — dizer que nao existe e diferente de ainda nao saber.
    loadingHistory: false,
    anamneses: [],
    anamneseHistories: {},
    loadingHistories: false,
  },
  mutations: {
    SET_MESSAGES(state, messages) {
      state.messages = messages;
    },
    ADD_MESSAGE(state, message) {
      state.messages.push(message);
    },
    SET_TYPING(state, isTyping) {
      state.isTyping = isTyping;
    },
    SET_ACTIVE_PATIENT(state, patientId) {
      state.activePatientId = patientId;
    },
    SET_CHAT_INITIATED(state, initiated) {
      state.chatInitiated = initiated;
    },
    SET_PATIENT_PHONE(state, phone) {
      state.patientPhone = phone;
    },
    SET_ANAMNESES(state, anamneses) {
      state.anamneses = anamneses;
    },
    SET_ANAMNESE_HISTORIES(state, { anamneseId, histories }) {
      state.anamneseHistories = {
        ...state.anamneseHistories,
        [anamneseId]: histories,
      };
    },
    SET_LOADING_HISTORIES(state, loading) {
      state.loadingHistories = loading;
    },
    SET_LOADING_HISTORY(state, loading) {
      state.loadingHistory = loading;
    }
  },
  actions: {
    async fetchHistory({ commit }, patientId) {
      commit("SET_LOADING_HISTORY", true);
      try {
        const { data } = await http.get(`/api/patient-chat/patient/${patientId}`);
        if (data.success) {
          commit("SET_MESSAGES", data.data);
          commit("SET_CHAT_INITIATED", data.data.length > 0);
          commit("SET_PATIENT_PHONE", data.patient_phone);
        }
      } catch (error) {
        console.error("Erro ao carregar histórico do chat:", error);
      } finally {
        // `finally`: se a busca falhar, o campo sai do limbo e volta a se
        // comportar como antes. Ficar "carregando" pra sempre esconderia um
        // paciente que de fato nao tem telefone.
        commit("SET_LOADING_HISTORY", false);
      }
    },
    async startChat({ commit, state }, { patientId, initialMessage, phone }) {
      try {
        commit("SET_TYPING", true);
        const { data } = await http.post("/api/patient-chat/start", {
          patient_id: patientId,
          initial_message: initialMessage,
          phone: phone
        });
        if (data.success) {
          commit("ADD_MESSAGE", data.message);
          commit("SET_CHAT_INITIATED", true);
          return { success: true };
        }
        return { success: false, error: data.error || "Falha ao iniciar a conversa." };
      } catch (error) {
        console.error("Erro ao iniciar chat:", error);
        const msg =
          error?.response?.data?.error ||
          "Não foi possível conectar ao serviço de WhatsApp.";
        return { success: false, error: msg };
      } finally {
        commit("SET_TYPING", false);
      }
    },
    // salvia-810: NUNCA adicionar a mensagem otimisticamente. So entra na tela o
    // que o backend confirmou que saiu de fato. O erro volta pra quem chamou, em
    // vez de morrer num console.error — antes, envio recusado pelo Twilio deixava
    // o medico achando que a mensagem tinha ido.
    async sendMessage({ commit, state }, { patientId, message }) {
      try {
        commit("SET_TYPING", true);
        const { data } = await http.post("/api/patient-chat/send", {
          patient_id: patientId,
          message: message
        });
        if (data.success) {
          commit("ADD_MESSAGE", data.data);
          // `aviso` != erro: a conversa estava fechada, o convite foi enviado e
          // a mensagem sai quando o paciente responder. O medico precisa saber,
          // mas nao e falha — quem chama decide como mostrar.
          return { success: true, aviso: data.aviso || null };
        }
        return { success: false, error: data.error || "A mensagem não foi enviada." };
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
        const msg =
          error?.response?.data?.error ||
          "Não foi possível enviar a mensagem. Ela NÃO chegou ao paciente.";
        return { success: false, error: msg };
      } finally {
        commit("SET_TYPING", false);
      }
    },
    receiveMessage({ commit, state }, message) {
      // Ignorar se a mensagem que chegou for a mesma que o médico acabou de enviar e já está na lista
      const exists = state.messages.find(m => m.id === message.id);
      if (!exists) {
        commit("ADD_MESSAGE", message);
      }
    },
    async fetchAnamneses({ commit }, patientId) {
      try {
        const { data } = await http.get(`/api/anamneses/by-patient/${patientId}`);
        if (data.status === "success") {
          commit("SET_ANAMNESES", Array.isArray(data.data) ? data.data : []);
        }
      } catch (error) {
        console.error("Erro ao carregar anamneses do paciente:", error);
      }
    },
    async fetchAnamneseHistories({ commit }, anamneseId) {
      try {
        commit("SET_LOADING_HISTORIES", true);
        const { data } = await http.get(
          `/api/anamneses/${anamneseId}/medical-histories`
        );
        const histories = Array.isArray(data?.data?.histories)
          ? data.data.histories
          : [];
        commit("SET_ANAMNESE_HISTORIES", { anamneseId, histories });
      } catch (error) {
        console.error("Erro ao carregar respostas da anamnese:", error);
        commit("SET_ANAMNESE_HISTORIES", { anamneseId, histories: [] });
      } finally {
        commit("SET_LOADING_HISTORIES", false);
      }
    }
  }
};
